import React, { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { connect } from 'react-redux';
import { ConfigurationFeatureFlag } from '@tink/config/src/tinkLinkConfig';
import { ErrorReason } from '@tink/core/src/tinkLinkErrorCodes';
import {
  ERROR_REASONS_TO_ALWAYS_RENDER,
  ERROR_REASONS_TO_NEVER_RENDER,
} from '../../../utilities/error';
import { ApplicationErrorResult } from '../../../models/ApplicationErrorResult';
import { postNotificationHandler as postNotificationHandlerImport } from '../../../utilities/post-notification-handler';
import { applicationParameters } from '../../../utilities/application-parameters';
import { useAnalytics } from '../../Analytics/AnalyticsProvider';
import { PageView } from '../../../models/analytics';

import { AppState, ConnectedPropsWithoutDispatch } from '../../../stores/redux-store';
import { Credentials } from '../../../models/credentials';
import { FlowFinished } from '../../../components/screens/FlowFinished/FlowFinished';
import { parentMessenger } from '../../Analytics/ParentApplicationEventMessenger';
import { useConfiguration } from '../../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import {
  VerboseErrorScreen,
  VerboseErrorScreenProps,
} from '../../../components/screens/VerboseErrorScreen/VerboseErrorScreen';
import { getEnumKeyByEnumValue } from '../../../utilities/utils';
import {
  AppStoreCredentialsError,
  AppStoreGeneralError,
  TinkErrorReason,
  VerboseError,
} from '../../../stores/app-state-errors';
import { useIsFlowHandledByRedirectHandler as useIsFlowHandledByRedirectHandlerImport } from '../../../utilities/useIsFlowHandledByRedirectHandler';
import { Executor } from './Executor';
import { messages } from './messages';
import { DetailedErrorScreen } from './DetailedErrorScreen';

const getTranslation = (errorReason?: TinkErrorReason) => {
  const key = getEnumKeyByEnumValue(ErrorReason, errorReason || '');

  return key && messages[key] ? messages[key] : messages.defaultError;
};

export type ErrorNotificationPresenterOwnProps = {
  error: AppStoreGeneralError | AppStoreCredentialsError;
  postNotificationHandler?: typeof postNotificationHandlerImport;
  useIsFlowHandledByRedirectHandler?: typeof useIsFlowHandledByRedirectHandlerImport;
  ErrorScreen?: React.ComponentType<VerboseErrorScreenProps>;
};

export type ErrorNotificationPresenterProps = PropsFromRedux & ErrorNotificationPresenterOwnProps;

export const ErrorNotificationPresenter: React.FC<ErrorNotificationPresenterProps> = ({
  error,
  credentials,
  redirectedCredentialsId,
  postNotificationHandler = postNotificationHandlerImport,
  useIsFlowHandledByRedirectHandler = useIsFlowHandledByRedirectHandlerImport,
  ErrorScreen = VerboseErrorScreen,
}) => {
  const { data: configurationData } = useConfiguration();
  const { tinklinkAnalytics } = useAnalytics();
  const intl = useIntl();

  const flowHandledByRedirectHandler =
    useIsFlowHandledByRedirectHandler({
      redirectedCredentialsId,
      appUri: applicationParameters.get('appUri'),
      credentials,
    }) && Credentials.hasError(credentials);

  const isErrorThatShouldAlwaysRedirect = ERROR_REASONS_TO_NEVER_RENDER.includes(error.errorReason);
  const isNotRedirectableError =
    ERROR_REASONS_TO_ALWAYS_RENDER.includes(error.errorReason) && !isErrorThatShouldAlwaysRedirect;
  const shouldRedirect =
    configurationData.featureFlags.includes(ConfigurationFeatureFlag.ERRORS_REDIRECT) ||
    isErrorThatShouldAlwaysRedirect;

  const verboseError: VerboseError = useMemo(() => {
    const { credentialsDetailedError, errorReason, errorStatus, errorType, providerName } = error;
    const finalErrorReason: TinkErrorReason = credentialsDetailedError
      ? credentialsDetailedError.details.reason
      : errorReason;

    const localizedMessage = intl.formatMessage(getTranslation(errorReason));
    const credentialsErrorMessage = credentialsDetailedError?.displayMessage;
    const finalLocalizedMessage = credentialsErrorMessage || localizedMessage;

    return {
      errorType,
      errorStatus,
      providerName,
      errorReason: finalErrorReason,
      localizedMessage: finalLocalizedMessage,
      credentialsDetailedErrorType: credentialsDetailedError?.type,
      credentialsDetailedDisplayMessage: credentialsDetailedError?.displayMessage,
      trackingId: applicationParameters.get('trackingId'),
      paymentRequestId: applicationParameters.get('paymentRequestId'),
      ...(credentials?.id && { credentialsId: credentials?.id }),
    };
  }, [credentials?.id, error, intl]);

  const postErrorNotification = useCallback(() => {
    const applicationErrorResult = new ApplicationErrorResult(verboseError);

    postNotificationHandler.execute(applicationErrorResult);
  }, [postNotificationHandler, verboseError]);

  useEffect(() => {
    if (flowHandledByRedirectHandler) {
      return;
    }

    const initialize = async () => {
      if (error.errorReason !== ErrorReason.REQUEST_FAILED_OAUTH_DESCRIBE) {
        parentMessenger.fireViewEvent(PageView.ERROR_SCREEN);
      }
    };

    initialize();
  }, [error, error.errorReason, flowHandledByRedirectHandler, tinklinkAnalytics]);

  if (flowHandledByRedirectHandler) {
    return <FlowFinished />;
  }

  // param validation failed
  if (isNotRedirectableError) {
    return (
      <ErrorScreen
        title={verboseError.localizedMessage}
        error={verboseError}
        viewEvent={PageView.ERROR_SCREEN}
      />
    );
  }

  // user has cancelled
  if (shouldRedirect) {
    return <Executor error={error} execute={postErrorNotification} />;
  }

  return (
    <DetailedErrorScreen
      verboseError={verboseError}
      primaryAction={postErrorNotification}
      viewEvent={PageView.ERROR_SCREEN}
    />
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    credentials: state.credentials.data,
    redirectedCredentialsId: state.application.redirectedCredentialsId,
  };
};

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedPropsWithoutDispatch<typeof connector>;

export const ErrorNotification = connector(ErrorNotificationPresenter);
